import React, { useMemo } from 'react';
import { Table, Input, InputNumber, Select, Button, DatePicker, Switch, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  PO_MILESTONE_TYPES,
  PO_MILESTONE_FULFILLMENT_LABELS,
  PO_PAYMENT_TERM_MILESTONE,
} from '@/constants/poMilestoneTypes';
import type { PoMilestoneInput } from '@/services/poMilestone.service';

const { Text } = Typography;

export interface MilestoneRowData extends PoMilestoneInput {
  key: string;
}

function defaultFulfillment(type: string): string {
  if (type === 'Advance') return 'SupplierAdvance';
  if (['Material Delivery', 'Equipment Delivery', 'Spare Parts Delivery', 'Consumables Delivery'].includes(type)) {
    return 'GRN';
  }
  if (
    ['Service Completion', 'Subcontract Progress', 'Installation', 'Testing', 'Commissioning', 'Inspection Acceptance'].includes(
      type
    )
  ) {
    return 'ServiceReceipt';
  }
  if (['Final Acceptance', 'Retention Release'].includes(type)) return 'ManualApproval';
  return 'ManualApproval';
}

export interface PoMilestoneScheduleEditorProps {
  rows: MilestoneRowData[];
  onChange: (rows: MilestoneRowData[]) => void;
  grandTotal: number;
  readOnly?: boolean;
  paymentTermType?: string;
}

export const PoMilestoneScheduleEditor: React.FC<PoMilestoneScheduleEditorProps> = ({
  rows,
  onChange,
  grandTotal,
  readOnly = false,
  paymentTermType,
}) => {
  const show = paymentTermType === PO_PAYMENT_TERM_MILESTONE;
  const totalPct = useMemo(() => rows.reduce((s, r) => s + (Number(r.percentage) || 0), 0), [rows]);
  const totalAmt = useMemo(() => rows.reduce((s, r) => s + (Number(r.milestone_amount) || 0), 0), [rows]);
  const pctOk = Math.abs(totalPct - 100) < 0.02;
  const amtOk = grandTotal > 0 && Math.abs(totalAmt - grandTotal) < 0.02;

  if (!show) return null;

  const updateRow = (key: string, field: keyof MilestoneRowData, value: unknown) => {
    onChange(
      rows.map((r) => {
        if (r.key !== key) return r;
        const next = { ...r, [field]: value } as MilestoneRowData;
        if (field === 'milestone_type') {
          next.fulfillment_method = defaultFulfillment(String(value));
        }
        if (field === 'percentage' && grandTotal > 0) {
          const pct = Number(value) || 0;
          next.milestone_amount = Math.round((grandTotal * pct) / 100 * 100) / 100;
        }
        if (field === 'milestone_amount' && grandTotal > 0) {
          const amt = Number(value) || 0;
          next.percentage = Math.round((amt / grandTotal) * 10000) / 100;
        }
        return next;
      })
    );
  };

  const addRow = () => {
    onChange([
      ...rows,
      {
        key: `ms-${Date.now()}`,
        milestone_number: rows.length + 1,
        milestone_name: '',
        milestone_type: 'Material Delivery',
        fulfillment_method: 'GRN',
        percentage: 0,
        milestone_amount: 0,
        tax_applicable: true,
        retention_applicable: false,
        retention_percentage: 0,
      },
    ]);
  };

  const removeRow = (key: string) => onChange(rows.filter((r) => r.key !== key));

  return (
    <div style={{ marginTop: 16, marginBottom: 16 }}>
      <Typography.Title level={5}>Milestone payment schedule</Typography.Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        Schedule must total 100% or match PO grand total ({grandTotal.toFixed(2)}).
      </Text>
      {!pctOk && !amtOk && rows.length > 0 && (
        <Text type="danger" style={{ display: 'block', marginBottom: 8 }}>
          Current total: {totalPct.toFixed(2)}% / {totalAmt.toFixed(2)} — adjust milestones.
        </Text>
      )}
      <Table
        size="small"
        pagination={false}
        rowKey="key"
        scroll={{ x: 1200 }}
        dataSource={rows}
        columns={[
          { title: '#', width: 40, render: (_, __, i) => i + 1 },
          {
            title: 'Name',
            dataIndex: 'milestone_name',
            width: 140,
            render: (v, r) =>
              readOnly ? v : <Input value={v} onChange={(e) => updateRow(r.key, 'milestone_name', e.target.value)} />,
          },
          {
            title: 'Type',
            dataIndex: 'milestone_type',
            width: 150,
            render: (v, r) =>
              readOnly ? v : (
                <Select
                  style={{ width: '100%' }}
                  value={v}
                  options={PO_MILESTONE_TYPES.map((t) => ({ value: t, label: t }))}
                  onChange={(val) => updateRow(r.key, 'milestone_type', val)}
                />
              ),
          },
          {
            title: 'Fulfillment',
            dataIndex: 'fulfillment_method',
            width: 130,
            render: (v) => PO_MILESTONE_FULFILLMENT_LABELS[v] ?? v,
          },
          {
            title: '%',
            dataIndex: 'percentage',
            width: 80,
            render: (v, r) =>
              readOnly ? (
                Number(v).toFixed(2)
              ) : (
                <InputNumber
                  min={0}
                  max={100}
                  value={v}
                  onChange={(val) => updateRow(r.key, 'percentage', val)}
                  style={{ width: '100%' }}
                />
              ),
          },
          {
            title: 'Amount',
            dataIndex: 'milestone_amount',
            width: 100,
            render: (v, r) =>
              readOnly ? (
                Number(v).toFixed(2)
              ) : (
                <InputNumber
                  min={0}
                  value={v}
                  onChange={(val) => updateRow(r.key, 'milestone_amount', val)}
                  style={{ width: '100%' }}
                />
              ),
          },
          {
            title: 'Expected',
            dataIndex: 'expected_date',
            width: 120,
            render: (v, r) =>
              readOnly ? (
                v ? dayjs(v).format('DD-MMM-YY') : '—'
              ) : (
                <DatePicker
                  style={{ width: '100%' }}
                  value={v ? dayjs(v) : null}
                  onChange={(d) => updateRow(r.key, 'expected_date', d ? d.format('YYYY-MM-DD') : undefined)}
                />
              ),
          },
          {
            title: 'Retention',
            width: 90,
            render: (_, r) =>
              readOnly ? (
                r.retention_applicable ? `${r.retention_percentage ?? 0}%` : '—'
              ) : (
                <Switch
                  size="small"
                  checked={!!r.retention_applicable}
                  onChange={(c) => updateRow(r.key, 'retention_applicable', c)}
                />
              ),
          },
          {
            title: '',
            width: 40,
            render: (_, r) =>
              readOnly || rows.length <= 1 ? null : (
                <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => removeRow(r.key)} />
              ),
          },
        ]}
      />
      {!readOnly && (
        <Button type="dashed" onClick={addRow} icon={<PlusOutlined />} style={{ marginTop: 8 }}>
          Add milestone
        </Button>
      )}
    </div>
  );
};

export default PoMilestoneScheduleEditor;
